import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../hooks/useConfirm';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { 
  Users, 
  Send, 
  Plus,
  Trash2,
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  UserCheck,
  Sparkles,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const COMPETENCIAS = [
  'Autogestão',
  'Foco em Resultados',
  'Comunicação',
  'Trabalho em Equipe',
  'Aprendizado Contínuo',
  'Pensamento Crítico',
  'Alinhamento com Valores da Empresa',
  'Atitude de Dono',
  'Inteligência Emocional',
  'Gestão de Pessoas'
];

const GerenciarFeedback180V2 = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [gerandoPerguntas, setGerandoPerguntas] = useState(false);
  const [funcionarios, setFuncionarios] = useState([]);
  const [gestorCargos, setGestorCargos] = useState([]);
  const [loadingGestores, setLoadingGestores] = useState(false);
  const [loadingFuncionarios, setLoadingFuncionarios] = useState(false);
  const [pendentes, setPendentes] = useState([]);
  const [concluidos, setConcluidos] = useState([]);
  const [grupoDetalhes, setGrupoDetalhes] = useState(null);
  const [modalDetalhesAberto, setModalDetalhesAberto] = useState(false);
  const [modalRespostasAberto, setModalRespostasAberto] = useState(false);
  const [respostasGrupo, setRespostasGrupo] = useState(null);
  const [loadingRespostas, setLoadingRespostas] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    gestor_id: '',
    colaboradores: [],
    competencias: []
  });
  
  const [perguntasGeradas, setPerguntasGeradas] = useState([]);
  const { confirm, ConfirmDialog } = useConfirm();

  useEffect(() => {
    carregarDadosIniciais();
  }, []);

  const carregarDadosIniciais = async () => {
    await carregarFuncionarios();
    await carregarGestores();
    await carregarPendentes();
    await carregarConcluidos();
  };

  const carregarGestores = async () => {
    setLoadingGestores(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/position-salaries/gestores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGestorCargos(response.data.map(g => g.name) || []);
    } catch (error) {
      console.error('Erro ao carregar cargos de gestor:', error);
    } finally {
      setLoadingGestores(false);
    }
  };

  const carregarFuncionarios = async () => {
    setLoadingFuncionarios(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFuncionarios(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
    } finally {
      setLoadingFuncionarios(false);
    }
  };

  const carregarPendentes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feedback180-v2/pendentes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendentes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar pendentes:', error);
      setPendentes([]);
    }
  };

  const carregarConcluidos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/feedback180-v2/concluidos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConcluidos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar concluídos:', error);
      setConcluidos([]);
    }
  };

  const handleVerDetalhes = (grupo) => {
    setGrupoDetalhes(grupo);
    setModalDetalhesAberto(true);
  };

  const handleVerRespostas = async (grupo) => {
    setLoadingRespostas(true);
    try {
      const token = localStorage.getItem('token');
      
      // Buscar todas as avaliações do grupo
      const response = await axios.get(`${API}/api/feedback180-v2/grupo/${grupo.grupo_id}/respostas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setRespostasGrupo({
        ...grupo,
        avaliacoes: response.data
      });
      setModalRespostasAberto(true);
    } catch (error) {
      console.error('Erro ao carregar respostas:', error);
      toast.error('Erro ao carregar respostas do feedback');
    } finally {
      setLoadingRespostas(false);
    }
  };

  const handleExportarExcel = () => {
    if (!respostasGrupo || !respostasGrupo.avaliacoes) {
      toast.error('Nenhum dado disponível para exportar');
      return;
    }

    try {
      // Preparar dados para exportação
      const dadosExportacao = [];
      
      respostasGrupo.avaliacoes.forEach((avaliacao) => {
        if (avaliacao.respostas && avaliacao.respostas.length > 0) {
          avaliacao.respostas.forEach((resposta) => {
            dadosExportacao.push({
              'Avaliador': avaliacao.avaliador_nome || 'N/A',
              'Avaliado': avaliacao.avaliado_nome || 'N/A',
              'Tipo de Avaliação': avaliacao.tipo_avaliacao === 'gestor_avalia_colaborador' 
                ? 'Gestor → Colaborador' 
                : 'Colaborador → Gestor',
              'Competência': resposta.competencia || 'N/A',
              'Pergunta': resposta.pergunta || 'N/A',
              'Nota (1-5)': resposta.resposta || 'N/A',
              'Data': avaliacao.respondido_em 
                ? new Date(avaliacao.respondido_em).toLocaleDateString('pt-BR')
                : 'N/A'
            });
          });
        }
      });

      // Criar planilha
      const ws = XLSX.utils.json_to_sheet(dadosExportacao);
      
      // Ajustar largura das colunas
      const colWidths = [
        { wch: 25 }, // Avaliador
        { wch: 25 }, // Avaliado
        { wch: 25 }, // Tipo de Avaliação
        { wch: 20 }, // Competência
        { wch: 60 }, // Pergunta
        { wch: 12 }, // Nota
        { wch: 12 }  // Data
      ];
      ws['!cols'] = colWidths;

      // Criar workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Respostas Feedback 180');

      // Nome do arquivo com data
      const nomeArquivo = `Feedback_180_${respostasGrupo.gestor_nome.replace(/\s+/g, '_')}_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.xlsx`;

      // Baixar arquivo
      XLSX.writeFile(wb, nomeArquivo);
      
      toast.success('Arquivo Excel exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast.error('Erro ao exportar arquivo Excel');
    }
  };


  const handleExcluirGrupo = async (grupoId) => {
    const ok = await confirm({
      title: 'Excluir grupo de feedbacks?',
      description: 'Tem certeza que deseja excluir este grupo de feedbacks? Esta ação não pode ser desfeita.',
      confirmLabel: 'Excluir',
      destructive: true,
    });
    if (!ok) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/feedback180-v2/grupo/${grupoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Grupo de feedbacks excluído com sucesso!');
      await carregarDadosIniciais();
    } catch (error) {
      console.error('Erro ao excluir grupo:', error);
      toast.error('Erro ao excluir grupo de feedbacks');
    } finally {
      setLoading(false);
    }
  };

  const adicionarColaborador = () => {
    setFormData({
      ...formData,
      colaboradores: [...formData.colaboradores, '']
    });
  };

  const removerColaborador = (index) => {
    const novosColaboradores = formData.colaboradores.filter((_, i) => i !== index);
    setFormData({ ...formData, colaboradores: novosColaboradores });
  };

  const atualizarColaborador = (index, value) => {
    const novosColaboradores = [...formData.colaboradores];
    novosColaboradores[index] = value;
    setFormData({ ...formData, colaboradores: novosColaboradores });
  };

  const toggleCompetencia = (comp) => {
    const competencias = formData.competencias.includes(comp)
      ? formData.competencias.filter(c => c !== comp)
      : [...formData.competencias, comp];
    setFormData({ ...formData, competencias });
  };

  const gerarPerguntas = async () => {
    if (formData.competencias.length === 0) {
      toast.error('Selecione pelo menos uma competência');
      return;
    }

    setGerandoPerguntas(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/feedback180-v2/gerar-perguntas`,
        { competencias: formData.competencias },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPerguntasGeradas(response.data.perguntas);
      toast.success('20 perguntas geradas com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar perguntas:', error);
      toast.error('Erro ao gerar perguntas com IA');
    } finally {
      setGerandoPerguntas(false);
    }
  };

  const criarFeedback = async () => {
    if (!formData.gestor_id) {
      toast.error('Selecione um gestor');
      return;
    }

    const colaboradoresFiltrados = formData.colaboradores.filter(c => c !== '');
    if (colaboradoresFiltrados.length === 0) {
      toast.error('Adicione pelo menos um colaborador');
      return;
    }

    if (formData.competencias.length === 0) {
      toast.error('Selecione pelo menos uma competência');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/feedback180-v2/criar`,
        {
          gestor_id: formData.gestor_id,
          colaboradores_ids: colaboradoresFiltrados,
          competencias: formData.competencias
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Feedbacks 180º criados com sucesso!');
      
      // Reset form
      setFormData({
        gestor_id: '',
        colaboradores: [],
        competencias: []
      });
      setPerguntasGeradas([]);
      
      // Recarregar listas
      await carregarPendentes();
    } catch (error) {
      console.error('Erro ao criar feedbacks:', error);
      toast.error('Erro ao criar feedbacks 180º');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Users className="h-8 w-8 mr-3 text-blue-600" />
          Gerenciar Feedbacks 180º
        </h1>
        <p className="text-gray-600 mt-1">
          Crie e gerencie avaliações 180º entre gestores e colaboradores
        </p>
      </div>

      <Tabs defaultValue="criar" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="criar">Criar Feedback 180º</TabsTrigger>
          <TabsTrigger value="pendentes">
            Feedbacks Pendentes ({pendentes.length})
          </TabsTrigger>
          <TabsTrigger value="concluidos">
            Feedbacks Concluídos ({concluidos.length})
          </TabsTrigger>
        </TabsList>

        {/* Aba Criar */}
        <TabsContent value="criar" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Criar Novo Feedback 180º</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Selecionar Gestor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gestor *
                </label>
                <select
                  value={formData.gestor_id}
                  onChange={(e) => setFormData({ ...formData, gestor_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  disabled={loadingGestores}
                >
                  <option value="">
                    {loadingGestores ? 'Carregando gestores...' : 'Selecione um gestor'}
                  </option>
                  {!loadingGestores && funcionarios.filter(f => gestorCargos.includes(f.cargo)).map((func) => (
                    <option key={func.id} value={func.id}>
                      {func.nome || func.nome_completo}
                    </option>
                  ))}
                </select>
                {loadingGestores && (
                  <div className="flex items-center text-sm text-gray-500 mt-1">
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    Carregando gestores...
                  </div>
                )}
              </div>

              {/* Colaboradores */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Colaboradores *
                </label>
                <div className="space-y-2">
                  {formData.colaboradores.map((colabId, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <select
                        value={colabId}
                        onChange={(e) => atualizarColaborador(index, e.target.value)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        disabled={loadingFuncionarios}
                      >
                        <option value="">
                          {loadingFuncionarios ? 'Carregando colaboradores...' : 'Selecione um colaborador'}
                        </option>
                        {!loadingFuncionarios && funcionarios
                          .filter(f => f.id !== formData.gestor_id)
                          .map((func) => (
                            <option key={func.id} value={func.id}>
                              {func.nome || func.nome_completo}
                            </option>
                          ))}
                      </select>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removerColaborador(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {loadingFuncionarios && (
                    <div className="flex items-center justify-center text-sm text-gray-500 py-2">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Carregando colaboradores...
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={adicionarColaborador}
                    className="w-full"
                    disabled={loadingFuncionarios}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Colaborador
                  </Button>
                </div>
              </div>

              {/* Competências */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Competências * (selecione as que deseja avaliar)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {COMPETENCIAS.map((comp) => (
                    <div
                      key={comp}
                      onClick={() => toggleCompetencia(comp)}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.competencias.includes(comp)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          formData.competencias.includes(comp)
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}>
                          {formData.competencias.includes(comp) && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-sm font-medium">{comp}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {formData.competencias.length} competência(s) selecionada(s)
                </p>
              </div>

              {/* Botão Gerar Perguntas */}
              <div className="border-t pt-4">
                <Button
                  onClick={gerarPerguntas}
                  disabled={gerandoPerguntas || formData.competencias.length === 0}
                  variant="outline"
                  className="w-full"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {gerandoPerguntas ? 'Gerando perguntas com IA...' : 'Gerar Perguntas (Prévia)'}
                </Button>
              </div>

              {/* Preview das perguntas */}
              {perguntasGeradas.length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Prévia das 20 Perguntas Geradas:</h3>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {perguntasGeradas.map((p, index) => (
                      <div key={index} className="p-2 bg-gray-50 rounded text-sm">
                        <strong>{index + 1}.</strong> {p.pergunta}
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {p.competencia}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Botão Criar */}
              <div className="flex justify-end space-x-3 border-t pt-4">
                <Button
                  onClick={criarFeedback}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {loading ? 'Criando...' : 'Criar Feedbacks 180º'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Pendentes */}
        <TabsContent value="pendentes" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-yellow-600" />
                <span>Feedbacks Pendentes</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendentes.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Nenhum feedback pendente
                  </h3>
                  <p className="text-gray-600">
                    Crie um novo feedback 180º na aba "Criar Feedback 180º"
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendentes.map((grupo) => (
                    <div key={grupo.grupo_id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg">{grupo.gestor_nome}</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            {grupo.total_colaboradores} colaborador(es)
                          </p>
                          {/* Lista de colaboradores */}
                          {grupo.colaboradores_nomes && grupo.colaboradores_nomes.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {grupo.colaboradores_nomes.map((nome, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  <Users className="h-3 w-3 mr-1" />
                                  {nome}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="text-yellow-700 border-yellow-300">
                            {grupo.respondidas}/{grupo.total_avaliacoes} Respondidas
                          </Badge>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(grupo.criado_em).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                        <div 
                          className="bg-yellow-500 h-2 rounded-full transition-all"
                          style={{ width: `${(grupo.respondidas / grupo.total_avaliacoes) * 100}%` }}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">
                            {grupo.pendentes} pendente(s)
                          </span>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleVerDetalhes(grupo)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Ver Detalhes
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleExcluirGrupo(grupo.grupo_id)}
                              disabled={loading}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Excluir
                            </Button>
                          </div>
                        </div>
                        
                        {/* Mostrar quem não respondeu ainda */}
                        {grupo.pendentes_detalhes && grupo.pendentes_detalhes.length > 0 && (
                          <div className="bg-red-50 border border-red-200 rounded p-2">
                            <p className="text-xs font-semibold text-red-700 mb-1">⚠️ Aguardando resposta de:</p>
                            <div className="flex flex-wrap gap-1">
                              {grupo.pendentes_detalhes.map((pendente, idx) => (
                                <Badge key={idx} variant="destructive" className="text-xs">
                                  {pendente.nome} ({pendente.tipo})
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Concluídos */}
        <TabsContent value="concluidos" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span>Feedbacks Concluídos</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {concluidos.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Nenhum feedback concluído ainda
                  </h3>
                  <p className="text-gray-600">
                    Feedbacks concluídos aparecerão aqui
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {concluidos.map((grupo) => (
                    <div key={grupo.grupo_id} className="border rounded-lg p-4 bg-green-50">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg">{grupo.gestor_nome}</h4>
                          <p className="text-sm text-gray-600 mb-2">
                            {grupo.total_colaboradores} colaborador(es)
                          </p>
                          {/* Lista de colaboradores */}
                          {grupo.colaboradores_nomes && grupo.colaboradores_nomes.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {grupo.colaboradores_nomes.map((nome, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  <Users className="h-3 w-3 mr-1" />
                                  {nome}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <Badge className="bg-green-600 text-white">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Concluído
                          </Badge>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(grupo.criado_em).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-green-700 font-medium">
                          {grupo.total_avaliacoes} avaliações respondidas
                        </span>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleVerRespostas(grupo)}
                            disabled={loadingRespostas}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            {loadingRespostas ? 'Carregando...' : 'Ver Respostas'}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleExcluirGrupo(grupo.grupo_id)}
                            disabled={loading}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Excluir
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Detalhes */}
      {modalDetalhesAberto && grupoDetalhes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setModalDetalhesAberto(false)}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Detalhes do Feedback 180º</h2>
              <button
                onClick={() => setModalDetalhesAberto(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Gestor</h3>
                <p className="text-gray-700">{grupoDetalhes.gestor_nome}</p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-2">Colaboradores ({grupoDetalhes.total_colaboradores})</h3>
                <div className="flex flex-wrap gap-2">
                  {grupoDetalhes.colaboradores_nomes && grupoDetalhes.colaboradores_nomes.map((nome, idx) => (
                    <Badge key={idx} variant="secondary">
                      <Users className="h-3 w-3 mr-1" />
                      {nome}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-2">Estatísticas</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600">Total de Avaliações</p>
                    <p className="text-2xl font-bold">{grupoDetalhes.total_avaliacoes}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600">Respondidas</p>
                    <p className="text-2xl font-bold text-green-600">{grupoDetalhes.respondidas}</p>
                  </div>
                  {grupoDetalhes.pendentes !== undefined && (
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-sm text-gray-600">Pendentes</p>
                      <p className="text-2xl font-bold text-yellow-600">{grupoDetalhes.pendentes}</p>
                    </div>
                  )}
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600">Criado em</p>
                    <p className="text-lg font-semibold">{new Date(grupoDetalhes.criado_em).toLocaleDateString('pt-BR')}</p>
                  </div>
                </div>
              </div>
              
              {grupoDetalhes.status && (
                <div>
                  <h3 className="text-lg font-semibold mb-2">Status</h3>
                  <Badge className={grupoDetalhes.status === 'concluido' ? 'bg-green-600' : 'bg-yellow-600'}>
                    {grupoDetalhes.status === 'concluido' ? 'Concluído' : 'Pendente'}
                  </Badge>
                </div>
              )}
            </div>
            
            <div className="mt-6 flex justify-end">
              <Button onClick={() => setModalDetalhesAberto(false)}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Respostas Completo */}
      {modalRespostasAberto && respostasGrupo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setModalRespostasAberto(false)}>
          <div className="bg-white rounded-lg p-6 max-w-7xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">Tabulação Completa de Respostas</h2>
                <p className="text-gray-600 mt-1">Gestor: {respostasGrupo.gestor_nome}</p>
              </div>
              <button
                onClick={() => setModalRespostasAberto(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Tabela de Respostas */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Avaliador</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Avaliado</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Tipo</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Pergunta</th>
                    <th className="border border-gray-300 px-4 py-3 text-center font-semibold">Nota (1-5)</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {respostasGrupo.avaliacoes && respostasGrupo.avaliacoes.length > 0 ? (
                    respostasGrupo.avaliacoes.map((avaliacao, idx) => (
                      avaliacao.respostas && avaliacao.respostas.length > 0 ? (
                        avaliacao.respostas.map((resposta, rIdx) => (
                          <tr key={`${idx}-${rIdx}`} className={rIdx === 0 ? 'border-t-2 border-gray-400' : ''}>
                            {rIdx === 0 && (
                              <>
                                <td className="border border-gray-300 px-4 py-2" rowSpan={avaliacao.respostas.length}>
                                  <div className="font-medium">{avaliacao.avaliador_nome || 'N/A'}</div>
                                </td>
                                <td className="border border-gray-300 px-4 py-2" rowSpan={avaliacao.respostas.length}>
                                  <div className="font-medium">{avaliacao.avaliado_nome || 'N/A'}</div>
                                </td>
                                <td className="border border-gray-300 px-4 py-2" rowSpan={avaliacao.respostas.length}>
                                  <Badge variant={avaliacao.tipo_avaliacao === 'gestor_avalia_colaborador' ? 'default' : 'secondary'}>
                                    {avaliacao.tipo_avaliacao === 'gestor_avalia_colaborador' ? 'Gestor → Colaborador' : 'Colaborador → Gestor'}
                                  </Badge>
                                </td>
                              </>
                            )}
                            <td className="border border-gray-300 px-4 py-2">
                              <div className="text-sm">{resposta.pergunta}</div>
                              {resposta.competencia && (
                                <Badge variant="outline" className="mt-1 text-xs">
                                  {resposta.competencia}
                                </Badge>
                              )}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center">
                              <span className={`font-bold text-lg ${
                                resposta.resposta >= 4 ? 'text-green-600' : 
                                resposta.resposta >= 3 ? 'text-yellow-600' : 
                                'text-red-600'
                              }`}>
                                {resposta.resposta || 'N/A'}
                              </span>
                            </td>
                            {rIdx === 0 && (
                              <td className="border border-gray-300 px-4 py-2" rowSpan={avaliacao.respostas.length}>
                                <div className="text-sm">
                                  {avaliacao.respondido_em ? new Date(avaliacao.respondido_em).toLocaleDateString('pt-BR') : 'N/A'}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr key={idx}>
                          <td colSpan="6" className="border border-gray-300 px-4 py-2 text-center text-gray-500">
                            Sem respostas para esta avaliação
                          </td>
                        </tr>
                      )
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="border border-gray-300 px-4 py-8 text-center text-gray-500">
                        Nenhuma resposta encontrada
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Estatísticas Resumidas */}
            {respostasGrupo.avaliacoes && respostasGrupo.avaliacoes.length > 0 && (
              <div className="mt-6 grid grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Total de Avaliações</p>
                  <p className="text-2xl font-bold">{respostasGrupo.avaliacoes.length}</p>
                </div>
                <div className="bg-green-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Respondidas</p>
                  <p className="text-2xl font-bold text-green-600">
                    {respostasGrupo.avaliacoes.filter(a => a.status === 'respondida').length}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Total de Respostas</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {respostasGrupo.avaliacoes.reduce((sum, a) => sum + (a.respostas?.length || 0), 0)}
                  </p>
                </div>
                <div className="bg-orange-50 p-4 rounded">
                  <p className="text-sm text-gray-600">Média Geral</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {(() => {
                      const todasRespostas = respostasGrupo.avaliacoes.flatMap(a => a.respostas || []);
                      const media = todasRespostas.length > 0 
                        ? (todasRespostas.reduce((sum, r) => sum + (r.resposta || 0), 0) / todasRespostas.length).toFixed(1)
                        : 0;
                      return media;
                    })()}
                  </p>
                </div>
              </div>
            )}
            
            <div className="mt-6 flex justify-between">
              <Button 
                variant="outline"
                onClick={handleExportarExcel}
              >
                📊 Exportar para Excel
              </Button>
              <Button onClick={() => setModalRespostasAberto(false)}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog />
    </div>
  );
};

export default GerenciarFeedback180V2;
